/**
 * Live Obligation Gantt — portfolio-wide obligation timeline with time-bar countdowns + Cognara notice drafter.
 */
import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { CalendarClock, Sparkles, Loader2, ArrowLeft, Search, AlertTriangle } from 'lucide-react';
import api from '@/lib/api';

const SLA = { OVERDUE: '#EF4444', URGENT: '#F97316', WARN: '#F59E0B', OK: '#22C55E' };
const CRIT = { HIGH: '#EF4444', MEDIUM: '#F59E0B', LOW: '#22C55E' };

export default function ContractsObligations() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [slaFilter, setSlaFilter] = useState('all');
  const [critFilter, setCritFilter] = useState('all');
  const [selected, setSelected] = useState(null);
  const [showNotice, setShowNotice] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const [notice, setNotice] = useState(null);

  useEffect(() => { (async () => {
    try { const r = await api.get('/contracts/portfolio/obligations'); setData(r.data); }
    catch { toast.error('Failed to load obligations'); } finally { setLoading(false); }
  })(); }, []);

  const rows = data?.obligations || [];
  const filtered = useMemo(() => rows.filter(r => {
    const t = q.toLowerCase();
    const okQ = !t || (r.description || '').toLowerCase().includes(t) || (r.project || '').toLowerCase().includes(t);
    const okS = slaFilter === 'all' || r.sla_status === slaFilter;
    const okC = critFilter === 'all' || r.criticality === critFilter;
    return okQ && okS && okC;
  }), [rows, q, slaFilter, critFilter]);

  // Gantt scale: min start date → max due date across visible rows
  const scale = useMemo(() => {
    const starts = filtered.map(r => new Date(r.bar_start || r.computed_due_date).getTime()).filter(Number.isFinite);
    const ends = filtered.map(r => new Date(r.computed_due_date).getTime()).filter(Number.isFinite);
    if (!starts.length || !ends.length) return null;
    const minT = Math.min(...starts, Date.now() - 30 * 86400000);
    const maxT = Math.max(...ends, Date.now() + 60 * 86400000);
    return { min: minT, max: maxT, span: Math.max(1, maxT - minT), nowPct: ((Date.now() - minT) / (maxT - minT)) * 100 };
  }, [filtered]);

  const draftNotice = async (r) => {
    setSelected(r); setShowNotice(true); setNotice(null); setDrafting(true);
    try { const res = await api.post(`/contracts/obligations/${r.id}/draft-notice`); setNotice(res.data); toast.success('Notice drafted'); }
    catch { toast.error('Draft failed'); } finally { setDrafting(false); }
  };

  const c = data?.counts || {};

  if (loading) return <DashboardLayout><div className="flex items-center justify-center h-96"><Loader2 className="w-8 h-8 animate-spin text-blue-500" /></div></DashboardLayout>;

  return (
    <DashboardLayout>
      <div className="space-y-4" data-testid="contracts-obligations">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/contracts')} data-testid="obl-back-btn"><ArrowLeft className="w-4 h-4" /></Button>
          <div>
            <h1 className="text-2xl font-bold text-foreground">Live Obligation Gantt</h1>
            <p className="text-sm text-muted-foreground mt-1">Time-bar countdowns · criticality heat-map · Cognara notice-letter drafter</p>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="obl-kpis">
          {[
            { label: 'Total', value: c.total || 0, color: '#94A3B8' },
            { label: 'Overdue', value: c.overdue || 0, color: '#EF4444' },
            { label: 'Urgent (≤3d)', value: c.urgent || 0, color: '#F97316' },
            { label: 'High Criticality', value: c.high_crit || 0, color: '#DC2626' },
            { label: 'On Track', value: (c.total || 0) - (c.overdue || 0) - (c.urgent || 0), color: '#22C55E' },
          ].map(kpi => (
            <Card key={kpi.label} className="bg-card border-border"><CardContent className="p-3">
              <p className="text-[10px] text-muted-foreground uppercase tracking-wider">{kpi.label}</p>
              <p className="text-xl font-bold" style={{ color: kpi.color }}>{kpi.value}</p>
            </CardContent></Card>
          ))}
        </div>

        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              <div className="relative flex-1 min-w-[200px] max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search description / project..." className="pl-10 bg-muted border-border text-foreground" data-testid="obl-search" />
              </div>
              <Select value={slaFilter} onValueChange={setSlaFilter}>
                <SelectTrigger className="w-40 bg-muted border-border text-foreground" data-testid="obl-sla-filter"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-card border-border">
                  {['all', 'OVERDUE', 'URGENT', 'WARN', 'OK'].map(s => <SelectItem key={s} value={s}>{s === 'all' ? 'All SLA' : s}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={critFilter} onValueChange={setCritFilter}>
                <SelectTrigger className="w-40 bg-muted border-border text-foreground" data-testid="obl-crit-filter"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-card border-border">
                  {['all', 'HIGH', 'MEDIUM', 'LOW'].map(s => <SelectItem key={s} value={s}>{s === 'all' ? 'All Criticality' : s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            {scale && (
              <div className="relative border border-border rounded-lg overflow-hidden bg-muted" data-testid="obl-gantt">
                {/* Timeline header */}
                <div className="grid grid-cols-[240px_1fr] text-[10px] text-muted-foreground uppercase tracking-wider py-2 border-b border-border">
                  <div className="px-3">Obligation · Contract</div>
                  <div className="relative h-4">
                    <span className="absolute left-0">{new Date(scale.min).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
                    <span className="absolute right-2">{new Date(scale.max).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
                    <span className="absolute" style={{ left: `${scale.nowPct}%`, transform: 'translateX(-50%)', color: '#8B5CF6' }}>TODAY</span>
                  </div>
                </div>
                {/* Rows */}
                <div className="max-h-[520px] overflow-y-auto">
                  {filtered.map(r => {
                    const startPct = ((new Date(r.bar_start).getTime() - scale.min) / scale.span) * 100;
                    const endPct = ((new Date(r.computed_due_date || r.bar_start).getTime() - scale.min) / scale.span) * 100;
                    const width = Math.max(2, endPct - startPct);
                    const barColor = SLA[r.sla_status] || SLA.OK;
                    return (
                      <div key={r.id} className="grid grid-cols-[240px_1fr] items-center py-1.5 border-b border-border/30 hover:bg-white/[0.02]" data-testid={`obl-gantt-row-${r.id}`}>
                        <div className="px-3 text-xs text-foreground truncate cursor-pointer" title={r.description} onClick={() => draftNotice(r)}>
                          <div className="truncate">{r.description}</div>
                          <div className="text-[10px] text-muted-foreground truncate">{r.contract_number || '—'} · {r.project}</div>
                        </div>
                        <div className="relative h-5">
                          {/* Today marker */}
                          <div className="absolute top-0 bottom-0 w-px bg-purple-500/70 z-10" style={{ left: `${scale.nowPct}%` }} />
                          {/* Bar */}
                          <div className="absolute h-3 top-1 rounded-md group cursor-pointer" style={{ left: `${startPct}%`, width: `${width}%`, background: `${barColor}44`, border: `1px solid ${barColor}` }} onClick={() => draftNotice(r)}>
                            <div className="absolute inset-0 flex items-center justify-end pr-1">
                              <Badge style={{ background: `${(CRIT[r.criticality] || '#94A3B8')}44`, color: CRIT[r.criticality] || '#94A3B8' }} className="text-[8px] px-1 py-0">{r.criticality?.[0] || '—'}</Badge>
                            </div>
                          </div>
                          {/* Days label */}
                          {r.days_left != null && <span className="absolute top-1 text-[9px] whitespace-nowrap" style={{ left: `calc(${endPct}% + 4px)`, color: barColor }}>{r.days_left < 0 ? `${Math.abs(r.days_left)}d overdue` : `${r.days_left}d`}</span>}
                        </div>
                      </div>
                    );
                  })}
                  {filtered.length === 0 && <p className="text-muted-foreground text-sm text-center py-8">No obligations match filters</p>}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <Dialog open={showNotice} onOpenChange={setShowNotice}>
          <DialogContent className="max-w-2xl bg-card border-border text-foreground" data-testid="obl-notice-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-purple-400" />Cognara Notice Letter — {selected?.obligation_id}</DialogTitle>
              <DialogDescription>Auto-drafted based on obligation status, criticality and time-bar.</DialogDescription>
            </DialogHeader>
            {drafting ? (
              <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-purple-500 mr-2" /><span className="text-sm text-muted-foreground">Drafting notice...</span></div>
            ) : notice ? (
              <div className="space-y-3 text-sm" data-testid="obl-notice-content">
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge style={{ background: '#8B5CF622', color: '#A78BFA' }}>{notice.notice_type}</Badge>
                  <Badge variant="outline">{notice.tone}</Badge>
                  <Badge variant="outline">To: {notice.recipient_role}</Badge>
                </div>
                <div className="rounded p-3" style={{ background: 'var(--muted)', border: '1px solid var(--border)' }}>
                  <p className="text-muted-foreground text-xs uppercase mb-1">Subject</p>
                  <p className="text-foreground font-semibold">{notice.subject}</p>
                </div>
                <div className="rounded p-3 max-h-64 overflow-y-auto whitespace-pre-wrap text-foreground" style={{ background: 'var(--muted)', border: '1px solid var(--border)' }}>{notice.body}</div>
                {notice.required_action && <div className="rounded p-2 text-xs" style={{ background: '#EF444422', color: '#F87171' }}><AlertTriangle className="w-3 h-3 inline mr-1" />Required action: {notice.required_action}</div>}
                {notice.references?.length > 0 && <div>
                  <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">References</p>
                  <div className="flex flex-wrap gap-1">{notice.references.map((r, i) => <Badge key={i} variant="outline" className="text-[9px]">{r}</Badge>)}</div>
                </div>}
              </div>
            ) : null}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
